import React, { useEffect, useMemo } from "react";

import { ChartWrapper, KPIs } from "components/reports-cashflow";
import { TablesWrapper } from "components/reports-cashflow/Tables/TablesWrapper";
import { useActionHook } from "hooks";
import { debounce, pickBy } from "lodash";
import { useReportsActions, useReportsQueriesSelector } from "store";

// API calls queue:
// 1. getCashflowSetsAction - on mount
// 2. getCashflowAumAction - when set is selected
// 3. getAllocationTargetAction - get after aum was got, each time
// 4. getCashflowGraphAction - each time when aum or allocation target is updated

interface Props {}

export const CashflowModel: React.FC<Props> = () => {
  const {
    getCashflowAumAction,
    getCashflowGraphAction,
    getAllocationTargetAction,
    updateAllocationTargetAction,
    getAllocationTableAction,
    updateCashflowAumAction
  } = useReportsActions();
  const { filters, currency, dateRange, exchangeMethod, use_accounting_date, cashflowSetId } =
    useReportsQueriesSelector();

  const {
    action: getAumData,
    loading: aumLoading,
    data: aumData = null,
    setData: setAumData
  } = useActionHook(getCashflowAumAction, {
    onSuccess: async (data) => {
      if (data?.id) {
        //if aum exist, get allocation target
        getAllocationTarget({ aum_id: data.id });
        if (!cashflowSetId) return;
        getAllocationTableData({
          set_id: cashflowSetId,
          currency,
          dateRange,
          exchangeDate: exchangeMethod,
          filters: pickBy(filters, (value) => value.length > 0)
        });
      }
    }
  });

  const { action: updateAumData } = useActionHook(updateCashflowAumAction, {
    onSuccess: (res) => {
      setAumData(res);
      if (!cashflowSetId) return;
      getAllocationTableData({
        set_id: cashflowSetId,
        currency,
        dateRange,
        exchangeDate: exchangeMethod,
        filters: pickBy(filters, (value) => value.length > 0)
      });
    }
  });

  const {
    data: allocationTableData,
    loading: allocationTableLoading,
    action: getAllocationTableData
  } = useActionHook(getAllocationTableAction);

  const {
    action: getAllocationTarget,
    loading: allocationTargetLoading,
    data: allocationTargetData = [],
    setData: setAllocationTargetData
  } = useActionHook(getAllocationTargetAction);

  const { action: updateAllocationTarget } = useActionHook(updateAllocationTargetAction, {
    onSuccess: async (data) => {
      setAllocationTargetData(data || []);
      //if allocation target is updated, get graph
      cashflowSetId &&
        (await debouncedGetGraph({
          filters,
          dateRange,
          exchangeDate: exchangeMethod,
          currency,
          set_id: cashflowSetId,
          use_accounting_date
        }));
    }
  });

  const { action: getGraph, data: graphData, loading: graphLoading } = useActionHook(getCashflowGraphAction);

  // Debounced version of the API call to prevent duplicate requests
  const debouncedGetGraph = useMemo(() => debounce(getGraph, 300), []);

  useEffect(() => {
    if (!cashflowSetId) return;
    getAumData({ set_id: cashflowSetId });
  }, [cashflowSetId]);

  useEffect(() => {
    if (!cashflowSetId || !aumData) return;
    // get graph when aum is updated, because allocation line depend on aum.aum_enabled
    debouncedGetGraph({
      filters,
      dateRange,
      exchangeDate: exchangeMethod,
      currency,
      set_id: cashflowSetId,
      use_accounting_date
    });
  }, [aumData, filters, currency, dateRange, exchangeMethod, use_accounting_date]);

  useEffect(() => {
    if (!cashflowSetId || !aumData || !allocationTargetData?.length) return;
    getAllocationTableData({
      set_id: cashflowSetId,
      currency,
      dateRange,
      exchangeDate: exchangeMethod,
      filters: pickBy(filters, (value) => value.length > 0)
    });
  }, [currency, dateRange, exchangeMethod, filters]);

  const loadingStatus = useMemo(() => {
    return {
      aumLoading,
      allocationTargetLoading,
      graphLoading
    };
  }, [aumLoading, allocationTargetLoading, graphLoading]);

  return (
    <>
      <KPIs />
      <ChartWrapper
        currentSetId={cashflowSetId}
        data={graphData}
        loadingStatus={loadingStatus}
        aumData={aumData}
        updateAumData={updateAumData}
        allocationTargetData={allocationTargetData}
        updateAllocationTarget={updateAllocationTarget}
      />
      <TablesWrapper
        getGraph={(set_id) =>
          getGraph({
            filters,
            dateRange,
            exchangeDate: exchangeMethod,
            currency,
            set_id,
            use_accounting_date
          })
        }
        // currentSetId={cashflowSetId}
        allocationTargetData={allocationTargetData}
        aumData={aumData}
        allocationTableData={allocationTableData}
        allocationTableLoading={allocationTableLoading}
      />
    </>
  );
};
